import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { AttemptListItem, MemberExam } from '@jupi/shared'
import { RemoteContent, Title, formatDate, formatDateTime, useSnackbar } from '@jupi/ui'
import { useExams, useMyAttempts, useStartAttempt } from '../../api/exams'
import { RESULT_COLOR, resultKey, textIn } from './examLabels'

// The club's knowledge tests and onboarding checks: what the member can sit,
// what is open, and how past attempts went. The marking and the rules (retakes,
// licences) are the server's; this only shows them.
export default function ExamsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()
  const exams = useExams()
  const attempts = useMyAttempts()
  const start = useStartAttempt()

  const begin = (exam: MemberExam) => {
    if (exam.openAttemptId) return navigate(`/club/exams/attempts/${exam.openAttemptId}`)
    start.mutate(
      { examId: exam.examId },
      {
        onSuccess: (view) => navigate(`/club/exams/attempts/${view.attempt.attemptId}`),
        onError: () => showSnackbar(t('exams.startFailed'), { severity: 'error' }),
      },
    )
  }

  return (
    <Box>
      <Title label={t('exams.title')} />
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('exams.intro')}
      </Typography>

      <RemoteContent isLoading={exams.isLoading} error={exams.error}>
        <Stack spacing={2}>
          {exams.data?.exams.length === 0 ? <Typography>{t('exams.none')}</Typography> : null}
          {exams.data?.exams.map((exam) => (
            <ExamCard key={exam.examId} exam={exam} language={i18n.language} busy={start.isPending} onStart={() => begin(exam)} />
          ))}
        </Stack>
      </RemoteContent>

      <Typography variant="h6" component="h2" sx={{ mt: 4, mb: 1 }}>
        {t('exams.history')}
      </Typography>
      <RemoteContent isLoading={attempts.isLoading} error={attempts.error}>
        {attempts.data?.attempts.length === 0 ? <Typography color="text.secondary">{t('exams.noHistory')}</Typography> : null}
        <Stack spacing={1}>
          {attempts.data?.attempts.map((a) => (
            <HistoryRow key={a.attemptId} attempt={a} language={i18n.language} onOpen={() => navigate(`/club/exams/attempts/${a.attemptId}`)} />
          ))}
        </Stack>
      </RemoteContent>
    </Box>
  )
}

function ExamCard({ exam, language, busy, onStart }: { exam: MemberExam; language: string; busy: boolean; onStart: () => void }) {
  const { t } = useTranslation()
  const blocked = exam.blocker !== null && exam.blocker !== 'in_progress'
  const lacking = exam.missingLicences.length > 0
  const action = exam.openAttemptId ? t('exams.continue') : t('exams.start')

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Typography variant="h6" component="h3">
            {textIn(exam.title, language, exam.defaultLocale)}
          </Typography>
          {exam.latest?.result ? (
            <Chip size="small" color={RESULT_COLOR[exam.latest.result]} label={t(resultKey(exam.latest.status, exam.latest.result))} />
          ) : null}
        </Stack>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          {textIn(exam.description, language, exam.defaultLocale)}
        </Typography>
        <Stack direction="row" sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" variant="outlined" label={t(`exams.kind.${exam.kind}`)} />
          <Chip size="small" variant="outlined" label={t('exams.questionCount', { count: exam.questionCount })} />
          <Chip
            size="small"
            variant="outlined"
            label={exam.timeLimitMinutes ? t('exams.minutes', { count: exam.timeLimitMinutes }) : t('exams.untimed')}
          />
          {exam.kind === 'quiz' ? <Chip size="small" variant="outlined" label={t('exams.passMark', { pct: exam.passMarkPct })} /> : null}
          {!exam.resumable ? <Chip size="small" variant="outlined" label={t('exams.singleSitting')} /> : null}
        </Stack>
        {lacking ? (
          <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
            {t('exams.missingLicences', { types: exam.missingLicences.map((l) => t(`exams.licence.${l}`)).join(', ') })}
          </Typography>
        ) : null}
        {blocked ? (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1.5 }}>
            {t(`exams.blocker.${exam.blocker}`)}
            {exam.blocker === 'already_passed' && exam.latest?.validUntil
              ? ` ${t('exams.validUntil', { date: formatDate(exam.latest.validUntil) })}`
              : ''}
          </Typography>
        ) : null}
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button variant="contained" size="large" disabled={busy || lacking || blocked} onClick={onStart}>
          {action}
        </Button>
      </CardActions>
    </Card>
  )
}

function HistoryRow({ attempt, language, onOpen }: { attempt: AttemptListItem; language: string; onOpen: () => void }) {
  const { t } = useTranslation()
  return (
    <Card variant="outlined">
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        sx={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%', boxSizing: 'border-box', p: 1.5 }}
      >
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box>
            <Typography>{textIn(attempt.examTitle, language)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(attempt.submittedAt ?? attempt.startedAt)}
              {attempt.score !== null && attempt.maxScore !== null ? ` · ${attempt.score}/${attempt.maxScore}` : ''}
            </Typography>
          </Box>
          <Chip
            size="small"
            color={attempt.result ? RESULT_COLOR[attempt.result] : 'default'}
            label={t(resultKey(attempt.status, attempt.result))}
          />
        </Stack>
      </Box>
    </Card>
  )
}
